import {
  Box,
  Button,
  capitalize,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { DataRequest, DataRequestState } from "../data";
import { useT } from "../../i18n";
import SearchField from "../SearchField";
import { Field, Formik } from "formik";
import ZahllaufFilterSelect from "./ZahllaufFilterSelect";
import UpdateZahllaufUmbennenButton from "./UpdateZahllaufUmbennenButton";
import useCustomFilters from "../data/useCustomFilters";
import { ColumnDef } from "@romeis/romeis-data-grid";
import { EingangsrechnungInfo } from "../../api";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import PaidIcon from "@mui/icons-material/Paid";
import { Block } from "@mui/icons-material";
import DatevProtokolleButton from "../rechnungen/DatevProtokolleButton";
import { formatAsMoney } from "../../formatterHelpers";

export type ZahllaufFilter = {
  search?: string;
  status?: string;
  zahllauf?: string;
  zahllaufBezahlt?: boolean;
  filtered?: string;
};

export type ZahllaufProps = Omit<DataRequest<ZahllaufFilter>, "filter"> &
  Partial<Pick<DataRequest<ZahllaufFilter>, "filter">>;

export interface ZahllaufSummenProps {
  setAnzahlAlleRechnungen: (value: number) => void;
  setSummeAlleRechnungen: (value: number) => void;
  setAnzahlSelectedRechnungen: (value: number) => void;
  setSummeSelectedRechnungen: (value: number) => void;
  setBeinhaltetNichtFreigegebeneRechnungen?: (value: boolean) => void;
  setAlleSelectedRechnungen: (value: string[]) => void;
  alleRechnungen?: string[];
}

export const belegstati: string[] = [
  "IN_ERKENNUNG",
  "OFFEN",
  "IN_PRUEFUNG",
  "FREIGEGEBEN",
  "ABGELEHNT",
  "IN_DATEV",
];

interface ZahllaufFilterBarProps {
  request: DataRequestState<ZahllaufFilter>;
  filteredStateFilter: string;
  children?: React.ReactNode;
}

export function ZahllaufFilterBar({
  request,
  filteredStateFilter,
  children,
}: ZahllaufFilterBarProps) {
  const { setFilter } = request;
  const { currentCustomFilter, setCustomField } =
    useCustomFilters<ZahllaufFilter>({
      tableName: "zahllauf",
      defaultFilter: {
        search: "",
        status: "_all",
        zahllauf: undefined,
        zahllaufBezahlt: request.filter.zahllaufBezahlt,
      },
    });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t: translation } = useT("zahllauf");

  useEffect(() => {
    setFilter({
      search: currentCustomFilter.search,
      status: currentCustomFilter.status,
      zahllauf: currentCustomFilter.zahllauf,
      zahllaufBezahlt: currentCustomFilter.zahllaufBezahlt,
      filtered: filteredStateFilter || undefined,
    });
  }, [currentCustomFilter, filteredStateFilter, setFilter]);

  const filterContent = (
    <>
      <SearchField
        value={currentCustomFilter.search}
        onChange={(val) => setCustomField("search", val)}
      />
      <FormControl size="small" sx={{ width: "150px", ml: isMobile ? 1 : 0 }}>
        <InputLabel>{capitalize(translation("document-status"))}</InputLabel>
        <Select
          label="Belegstatus"
          size="small"
          value={currentCustomFilter.status ?? "_all"}
          onChange={(event) => setCustomField("status", event.target.value)}
        >
          <MenuItem key="_all" value="_all">
            Alle
          </MenuItem>
          {belegstati.map((belegstatus) => (
            <MenuItem key={belegstatus} value={belegstatus}>
              {belegstatus
                .split("_")
                .map((wort) =>
                  capitalize(wort.toLowerCase().replace("ue", "ü"))
                )
                .join(" ")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Formik
        initialValues={{ zahllauf: currentCustomFilter.zahllauf }}
        onSubmit={() => {}}
      >
        <>
          <Field
            name="zahllauf"
            component={ZahllaufFilterSelect}
            label={"Zahllauf"}
            required={false}
            setZahllauf={(val: any) => setCustomField("zahllauf", val)}
            zahllaufBezahlt={false}
          />
          <UpdateZahllaufUmbennenButton
            zahllaufId={currentCustomFilter.zahllauf}
            setZahllaufId={(val: any) => setCustomField("zahllauf", val)}
          />
        </>
      </Formik>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        position: "relative",
        width: "100%",
      }}
    >
      {isMobile ? (
        <Stack direction="row">
          {filterContent}
          {children}
        </Stack>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {filterContent}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function useZahllaufColumns(options?: {
  removeHyphensFromHeaders?: boolean;
}): Array<ColumnDef<EingangsrechnungInfo>> {
  const { t: translation } = useT("zahllauf");
  const money = (v: unknown) => (typeof v === "number" ? formatAsMoney(v) : "");
  const formatGermanDate = (value: unknown) => {
    const date = value as Date | undefined;
    if (!date || isNaN(date.getTime())) return "";
    return `${("0" + date.getDate()).slice(-2)}.${(
      "0" +
      (date.getMonth() + 1)
    ).slice(-2)}.${date.getFullYear()}`;
  };

  const formatHeader = (text: string) =>
    options?.removeHyphensFromHeaders ? text.replace(/-/g, "") : text;

  return useMemo(
    () => [
      {
        id: "zahlungkw",
        field: "zahlungkw",
        header: formatHeader(capitalize(translation("calendar-week-short"))),
        type: "number",
      },
      {
        id: "skontoDatum",
        field: "skontoDatum",
        header: formatHeader(capitalize(translation("discount-due-date"))),
        valueGetter: (row) => {
          return row.skontoDatum ? new Date(row.skontoDatum) : undefined;
        },
        renderCell: ({ value }) => {
          const date = value as Date | undefined;
          if (!date) return "";
          return formatGermanDate(date);
        },
        type: "date",
      },
      {
        id: "faelligOhneSkonto",
        field: "faelligOhneSkonto",
        header: formatHeader(capitalize(translation("due-date"))),
        valueGetter: (row) => {
          return row.faelligOhneSkonto
            ? new Date(row.faelligOhneSkonto)
            : undefined;
        },
        renderCell: ({ value }) => {
          const date = value as Date | undefined;
          if (!date) return "";
          return formatGermanDate(date);
        },
        type: "date",
      },
      {
        id: "lieferantName",
        field: "lieferantName",
        header: formatHeader(capitalize(translation("supplier"))),
        type: "string",
      },
      {
        id: "rechnungsNummer",
        field: "rechnungsNummer",
        header: formatHeader(capitalize(translation("invoice-number-short"))),
        type: "string",
      },
      {
        id: "gutschriftenRechnungsnummern",
        field: "gutschriftenRechnungsnummern",
        header: formatHeader(capitalize(translation("credits"))),
        type: "string",
      },
      {
        id: "rechnungsDatum",
        field: "rechnungsDatum",
        header: formatHeader(capitalize(translation("invoice-date"))),
        type: "date",
        valueGetter: (row) => {
          return row.rechnungsDatum ? new Date(row.rechnungsDatum) : undefined;
        },
        renderCell: ({ value }) =>
          value
            ? (value as Date).toLocaleDateString(undefined, {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })
            : translation("no-date"),
      },
      {
        id: "belegStatus",
        field: "belegStatus",
        header: formatHeader(capitalize(translation("document-status"))),
        renderCell: ({ row, value }) => {
          const id = row.id;
          switch (value) {
            case "IN_ERKENNUNG":
              return (
                <Chip
                  icon={<TextIncreaseIcon />}
                  title={"In Erkennung"}
                  label={"in Erkennung"}
                ></Chip>
              );
            case "OFFEN":
              return (
                <Chip
                  icon={<SpellcheckIcon />}
                  title={"Offen"}
                  color={"secondary"}
                  label={"offen"}
                ></Chip>
              );
            case "IN_PRUEFUNG":
              return (
                <Chip
                  icon={<PlaylistAddCheckCircleIcon />}
                  title={"In Prüfung"}
                  color={"warning"}
                  label={"in Prüfung"}
                ></Chip>
              );
            case "FREIGEGEBEN":
              return (
                <Chip
                  icon={<TaskAltIcon />}
                  title={"Freigegeben"}
                  color={"success"}
                  label={"freigegeben"}
                ></Chip>
              );
            case "ABGELEHNT":
              return (
                <Chip
                  icon={<HighlightOffIcon />}
                  title={"Abgelehnt"}
                  color={"error"}
                  label={"abgelehnt"}
                ></Chip>
              );
            case "IN_DATEV":
              return (
                <DatevProtokolleButton
                  id={id}
                  label={"in DATEV"}
                  color={"success"}
                  title={"Verarbeitet durch DATEV"}
                />
              );
            case "VERARBEITUNG_DATEV":
              return (
                <DatevProtokolleButton
                  id={id}
                  label={"Verarbeitung DATEV"}
                  color={"warning"}
                  title={"Verarbeitung durch DATEV"}
                />
              );
            case "FEHLER_BEI_UEBERTRAG_DATEV":
              return (
                <DatevProtokolleButton
                  id={id}
                  label={"Fehler DATEV"}
                  color={"error"}
                  title={"Fehler bei Übertrag nach Datev"}
                />
              );
            case "FEHLER_GOOGLE":
              return (
                <DatevProtokolleButton
                  id={id}
                  label={"Fehler Erkennung"}
                  color={"warning"}
                  title={"Fehler Erkennung Google"}
                />
              );
            case "BEZAHLT":
              return (
                <Chip
                  icon={<PaidIcon />}
                  title={"Bezahlt"}
                  color={"success"}
                  label={"bezahlt"}
                ></Chip>
              );
            case "STORNIERT":
              return (
                <Chip
                  icon={<Block />}
                  title={"Storniert"}
                  color={"secondary"}
                  label={"storniert"}
                ></Chip>
              );
            default:
              return <Chip icon={<Block />} label={"unbekannt"}></Chip>;
          }
        },
        type: "string",
      },
      {
        id: "nettoBetrag",
        field: "nettoBetrag",
        header: formatHeader(capitalize(translation("payment-amount-net"))),
        align: "right",
        type: "number",
        valueFormatter: ({ value }) => money(value),
        renderCell: ({ value }) => money(value),
      },
      {
        id: "umsatzsteuer",
        field: "umsatzsteuer",
        header: formatHeader(capitalize(translation("sales-tax-header"))),
        align: "right",
        type: "number",
        valueFormatter: ({ value }) => money(value),
        renderCell: ({ value }) => money(value),
      },
      {
        id: "bruttoBetrag",
        field: "bruttoBetrag",
        header: formatHeader(capitalize(translation("payment-amount-gross"))),
        align: "right",
        type: "number",
        valueFormatter: ({ value }) => money(value),
        renderCell: ({ value }) => money(value),
      },
      {
        id: "skontoBetrag",
        field: "skontoBetrag",
        header: formatHeader(capitalize(translation("discount-amount"))),
        align: "right",
        type: "number",
        valueFormatter: ({ value }) => money(value),
        renderCell: ({ value }) => money(value),
      },
      {
        id: "rechnungsBetrag",
        field: "rechnungsBetrag",
        header: formatHeader(capitalize(translation("payment-amount"))),
        align: "right",
        type: "number",
        valueFormatter: ({ value }) => money(value),
        renderCell: ({ value }) => money(value),
      },
      {
        id: "gesamtGutschrift",
        field: "gesamtGutschrift",
        header: formatHeader(capitalize(translation("credits"))),
        align: "right",
        type: "number",
        valueFormatter: ({ value }) => money(value),
        renderCell: ({ value }) => money(value),
      },
    ],
    [translation, formatHeader]
  );
}
